import React, {
  forwardRef,
  MouseEvent,
  useCallback,
  useImperativeHandle,
} from 'react';

import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  InitialTableState,
  Row,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import {
  useVirtualizer,
  VirtualItem,
  Virtualizer,
} from '@tanstack/react-virtual';

import { usePersistedJsonState } from '@betterfunnel/react-hooks';
import Checkbox from '@betterfunnel/ui-core/Checkbox';
import IconButton from '@betterfunnel/ui-core/IconButton';
import MenuButton, { MenuOption } from '@betterfunnel/ui-core/MenuButton';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/solid';
import { classNames } from '../../utils';
import ColumnManagementDrawer from './ColumnManagementDrawer';
import './CustomDataGrid.css';
import { getCommonPinningStyles } from './utils';

const AnimateCell = () => (
  <div className="h-2.5 w-1/2 max-w-12 min-w-7 animate-pulse rounded-md bg-gray-300"></div>
);

type RowClassName<T> = string | ((row: Row<T>) => string);
type CellClassName<T> =
  | string
  | ((cell: Cell<T, unknown>, row: Row<T>) => string);

export interface CustomDataGridHandle<T> {
  getTableInstance: () => Table<T>;
}

interface Props<T> {
  logicalName: string;
  columns: ColumnDef<T>[];
  data?: T[];
  initialState?: InitialTableState;
  tableClassName?: string;
  tableHeadClassName?: string;
  tableBodyClassName?: string;
  rowClassName?: RowClassName<T>;
  cellClassName?: CellClassName<T>;
  onRowClick?: (row: T, e: MouseEvent) => void;
  onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
  virtualized?: boolean;
  onReachedEnd?: () => void;

  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: T[]) => void;

  enableSorting?: boolean;
}

export const CustomDataGrid = forwardRef<CustomDataGridHandle<any>, Props<any>>(
  function CustomDataGrid<T>(
    {
      logicalName,
      data,
      columns,
      initialState,
      tableClassName: tableClassName,
      tableHeadClassName,
      tableBodyClassName,
      rowClassName: rowClassName,
      cellClassName: cellClassName,
      onRowClick,
      onCellClick,
      virtualized = true,
      onReachedEnd,
      enableRowSelection,
      onRowSelectionChange,
      enableSorting,
    }: Props<T>,
    ref: React.Ref<CustomDataGridHandle<T>>,
  ) {
    const [cachedInitialState, setCachedInitialState] = usePersistedJsonState<
      InitialTableState | undefined
    >('custom-data-grid-state:' + logicalName + ':' + window.location.pathname);
    const [, setRowSelection] = React.useState<Record<string, boolean>>({});

    const _onCellClick = useCallback(
      (cell: Cell<T, unknown>, e: MouseEvent) => {
        // to avoid a situation where a user accisentally clicks near a checkbox
        if (cell.column.id === 'select') {
          e.stopPropagation();
          e.preventDefault();
          cell.row.toggleSelected();
          return;
        }
        onCellClick?.(cell, e);
      },
      [onCellClick],
    );

    const _data: T[] =
      data ||
      ([
        { id: 'loading-item-1' },
        { id: 'loading-item-2' },
        { id: 'loading-item-3' },
        { id: 'loading-item-4' },
        { id: 'loading-item-5' },
      ] as any);

    const table = useReactTable<T>({
      data: _data,
      columns: [
        ...(enableRowSelection
          ? ([
              {
                id: 'select',
                size: 40,
                header: ({ table }) => (
                  <Checkbox
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={(_, e) =>
                      table.getToggleAllRowsSelectedHandler()(e)
                    }
                  />
                ),
                cell: ({ row }) => (
                  <Checkbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={(c) => {
                      row.toggleSelected(c);
                    }}
                  />
                ),
              },
            ] satisfies ColumnDef<T>[])
          : []),
        ...columns,
      ],
      defaultColumn: {
        minSize: 60,
        maxSize: 800,
      },
      columnResizeMode: 'onChange',
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      // debugTable: true,
      // debugHeaders: true,
      // debugColumns: true,
      enableRowSelection: enableRowSelection,
      onRowSelectionChange: (updater) => {
        if (!updater) return;

        setRowSelection((old) => {
          const newState = updater instanceof Function ? updater(old) : updater;
          table.setState((s) => {
            s.rowSelection = newState;
            return s;
          });

          return newState;
        });

        setTimeout(() => {
          const rows = table
            .getRowModel()
            .rows.filter((row) => row.getIsSelected());
          onRowSelectionChange?.(rows.map((row) => row.original));
        }, 0);
      },
      enableColumnPinning: true,
      enableSorting: enableSorting ?? false,
      initialState: {
        ...cachedInitialState,
        ...initialState,
        columnVisibility: {
          ...initialState?.columnVisibility,
          ...cachedInitialState?.columnVisibility,
        },
        columnSizing: {
          ...initialState?.columnSizing,
          ...cachedInitialState?.columnSizing,
        },
      },
      onStateChange: (updater) => {
        if (!updater) return;

        setCachedInitialState((old) => {
          try {
            const newState =
              updater instanceof Function
                ? updater((old || {}) as any)
                : updater;

            return {
              columnOrder: newState.columnOrder,
              columnSizing: newState.columnSizing,
              columnVisibility: newState.columnVisibility,
            };
          } catch (_) {
            return old;
          }
        });
      },
    });

    const [manageColumnsOpen, setManageColumnsOpen] = React.useState(false);

    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
      count: _data?.length ?? 0,
      estimateSize: () => 72, // estimate row height for accurate scrollbar dragging
      getScrollElement: () => tableContainerRef.current,
      // measure dynamic row height, except in firefox because it measures table border height incorrectly
      measureElement:
        typeof window !== 'undefined' &&
        navigator.userAgent.indexOf('Firefox') === -1
          ? (element) => element?.getBoundingClientRect().height
          : undefined,
      overscan: 5,
      enabled: virtualized,
    });

    /**
     * Instead of calling `column.getSize()` on every render for every header
     * and especially every data cell (very expensive),
     * we will calculate all column sizes at once at the root table level in a useMemo
     * and pass the column sizes down as CSS variables to the <table> element.
     */
    const columnSizeVars = React.useMemo(() => {
      const headers = table.getFlatHeaders();
      const colSizes: { [key: string]: number } = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
      }
      return colSizes;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      table.getState().columnSizingInfo,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      table.getState().columnSizing,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      table.getState().columnVisibility,
      columns,
    ]);

    // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
    const onScrolled = React.useCallback(
      (containerRefElement?: HTMLDivElement | null) => {
        if (containerRefElement) {
          const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
          // once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
          if (scrollHeight - scrollTop - clientHeight < 500) {
            onReachedEnd?.();
          }
        }
      },
      [onReachedEnd],
    );

    // a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
    React.useEffect(() => {
      onScrolled(tableContainerRef.current);
    }, [onScrolled]);

    useImperativeHandle(ref, () => ({
      getTableInstance: () => table,
    }));

    return (
      <div
        className={classNames(
          virtualized && 'absolute inset-0 h-full',
          'block w-full overflow-auto',
        )}
        ref={tableContainerRef}
        onScroll={(e) => onScrolled(e.currentTarget)}
      >
        <ColumnManagementDrawer
          open={manageColumnsOpen}
          onClose={() => {
            setManageColumnsOpen(false);
          }}
          table={table}
        />

        <div
          className={classNames('divTable relative min-w-full', tableClassName)}
          style={{
            ...columnSizeVars, // Define column sizes on the <table> element
            width: table.getTotalSize(),
          }}
        >
          <div
            className={classNames(
              'thead border-b border-gray-300',
              tableHeadClassName,
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <div key={headerGroup.id} className="tr">
                {headerGroup.headers.map((header) => (
                  <div
                    key={header.id}
                    className="th group flex items-center py-3.5 ps-5 pr-3 text-left text-sm font-semibold text-gray-900"
                    style={{
                      ...(header.column?.getIsPinned()
                        ? getCommonPinningStyles(header.column as any)
                        : {}),
                      width: `calc(var(--header-${header?.id}-size) * 1px)`,
                    }}
                  >
                    <div className="-my-2 flex flex-1 shrink flex-row items-center gap-1 truncate py-2">
                      <span className="truncate">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </span>
                      {header.column.getCanSort() &&
                        !header.column.getIsResizing() && (
                          <IconButton
                            variant="ternary"
                            className={classNames(
                              !header.column.getIsSorted()
                                ? 'hidden text-gray-300 group-hover:block'
                                : 'text-gray-600',
                            )}
                            onClick={() => header.column.toggleSorting()}
                          >
                            {header.column.getIsSorted() === 'asc' ? (
                              <ArrowUpIcon className="h-4 w-4" />
                            ) : (
                              <ArrowDownIcon className="h-4 w-4" />
                            )}
                          </IconButton>
                        )}
                    </div>

                    {!header.column.getIsResizing() &&
                      header.id !== 'select' && (
                        <MenuButton
                          items={
                            [
                              header.column.getCanHide()
                                ? {
                                    id: 'hide-column',
                                    title: 'Hide Column',
                                    onClick: () =>
                                      header.column.toggleVisibility(false),
                                  }
                                : null,

                              {
                                id: 'manage-columns',
                                title: 'Manage Columns',
                                onClick: () => {
                                  setManageColumnsOpen(true);
                                },
                              },
                            ].filter(Boolean) as MenuOption[]
                          }
                          customButton={({ open }) => (
                            <IconButton
                              variant="ternary"
                              className={classNames(
                                'shrink-0',
                                !open && 'hidden group-hover:block',
                              )}
                            >
                              <EllipsisVerticalIcon className="h-4 w-4 text-gray-600" />
                            </IconButton>
                          )}
                        />
                      )}

                    <div
                      {...{
                        onDoubleClick: () => header.column.resetSize(),
                        onMouseDown: header.getResizeHandler(),
                        onTouchStart: header.getResizeHandler(),
                        className: `resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                        }`,
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* When resizing any column we will render this special memoized version of our table body */}
          {table.getState().columnSizingInfo.isResizingColumn ? (
            <MemoizedTableBody
              table={table}
              rowVirtualizer={rowVirtualizer}
              tableBodyClassName={tableBodyClassName}
              rowClassName={rowClassName}
              cellClassName={cellClassName}
              onRowClick={onRowClick}
              onCellClick={_onCellClick}
              virtualized={virtualized}
              loading={!data}
            />
          ) : (
            <TableBody
              table={table}
              rowVirtualizer={rowVirtualizer}
              tableBodyClassName={tableBodyClassName}
              rowClassName={rowClassName}
              cellClassName={cellClassName}
              onRowClick={onRowClick}
              onCellClick={_onCellClick}
              virtualized={virtualized}
              loading={!data}
            />
          )}
        </div>
      </div>
    );
  },
);

// un-memoized normal table body component - see memoized version below
function TableBody<T>({
  table,
  tableBodyClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  onCellClick,
  ...props
}: {
  table: Table<T>;
  tableBodyClassName?: string;
  rowClassName?: RowClassName<T>;
  cellClassName?: CellClassName<T>;
  onRowClick?: (row: T, e: MouseEvent) => void;
  onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
  loading?: boolean;
} & (
  | {
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
      virtualized: true;
    }
  | {
      virtualized?: false;
    }
)) {
  return (
    <div
      className={classNames('tbody', tableBodyClassName)}
      style={
        props.virtualized
          ? {
              height: `${props.rowVirtualizer.getTotalSize()}px`, // tells scrollbar how big the table is
              position: 'relative', // needed for absolute positioning of rows
            }
          : {}
      }
    >
      <TableBodyContent
        table={table}
        tableBodyClassName={tableBodyClassName}
        rowClassName={rowClassName}
        cellClassName={cellClassName}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        {...props}
      />
    </div>
  );
}

function TableBodyContent<T>({
  table,
  tableBodyClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  onCellClick,
  ...props
}: {
  table: Table<T>;
  tableBodyClassName?: string;
  rowClassName?: RowClassName<T>;
  cellClassName?: CellClassName<T>;
  onRowClick?: (row: T, e: MouseEvent) => void;
  onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
  loading?: boolean;
} & (
  | {
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
      virtualized: true;
    }
  | {
      virtualized?: false;
    }
)) {
  if (props.virtualized) {
    return props.rowVirtualizer.getVirtualItems().map((virtualRow) => {
      const row = table.getRowModel().rows[virtualRow.index];
      return (
        <TableRow
          key={row.id}
          row={row}
          rowVirtualizer={props.rowVirtualizer}
          virtualRow={virtualRow}
          rowClassName={rowClassName}
          cellClassName={cellClassName}
          onRowClick={onRowClick}
          onCellClick={onCellClick}
          virtualized
          loading={props.loading}
        />
      );
    });
  }

  return table.getRowModel().rows.map((row) => {
    return (
      <TableRow
        key={row.id}
        row={row}
        rowClassName={rowClassName}
        cellClassName={cellClassName}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        loading={props.loading}
      />
    );
  });
}

// special memoized wrapper for our table body that we will use during column resizing
const MemoizedTableBody = React.memo(
  TableBody,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof TableBody;

function TableRow<T>({
  row,
  rowClassName,
  cellClassName,
  onRowClick,
  onCellClick,
  ...props
}:
  | {
      row: Row<T>;
      virtualRow: VirtualItem;
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
      rowClassName?: RowClassName<T>;
      cellClassName?: CellClassName<T>;
      onRowClick?: (row: T, e: MouseEvent) => void;
      onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
      loading?: boolean;
      virtualized: true;
    }
  | {
      row: Row<T>;
      rowClassName?: RowClassName<T>;
      cellClassName?: CellClassName<T>;
      onRowClick?: (row: T, e: MouseEvent) => void;
      onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
      loading?: boolean;
      virtualized?: false;
    }) {
  return (
    <div
      ref={(node) => {
        props.virtualized && props.rowVirtualizer.measureElement(node);
      }}
      key={row.id}
      className={classNames(
        'tr border-b border-gray-200 hover:bg-gray-50',
        typeof rowClassName === 'function' ? rowClassName(row) : rowClassName,
      )}
      data-index={props.virtualized && props.virtualRow.index}
      style={
        props.virtualized
          ? {
              position: 'absolute',
              transform: `translateY(${props.virtualRow.start}px)`, // this should always be a `style` as it changes on scroll
              width: '100%',
            }
          : {}
      }
      onClick={(e) => !props.loading && onRowClick?.(row.original, e)}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <div
            key={cell.id}
            className={classNames(
              'td overflow-hidden px-5 py-5 text-sm whitespace-nowrap text-gray-900',
              typeof cellClassName === 'function'
                ? cellClassName(cell, row)
                : cellClassName,
            )}
            style={{
              ...(cell.column.getIsPinned()
                ? getCommonPinningStyles(cell.column as any)
                : {}),
              width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
            }}
            onClick={(e) => {
              !props.loading && onCellClick?.(cell, e);
            }}
          >
            {!props.loading &&
              flexRender(cell.column.columnDef.cell, cell.getContext())}
            {props.loading &&
              flexRender(() => <AnimateCell />, cell.getContext())}
          </div>
        );
      })}
    </div>
  );
}
